import {get, isUndefined, orderBy} from "lodash";
import {IStatCell} from "modules/reducers";
import {DirectionFilter, PlayerPositionEnum, TeamPosition} from "modules/enums";
import {IPlayer, ISquad, IStatsFilter} from "modules/types";
import {playerPositions} from "modules/constants";
import {getPlayerName} from "modules/utils/helpers";

export interface IStatsTableItem {
	id: number;
	firstName?: string;
	lastName?: string;
	squadId: number;
	name: string;
	team: string;
	pos: string;
	position: PlayerPositionEnum | TeamPosition.DF;

	passingTouchdowns?: number | null;
	passingYards?: number | null;
	passingYardsAverage?: number | null;
	passingCompletionsPercentage?: number | string | null;
	passingInterceptions?: number | null;
	rushingTouchdowns?: number | null;
	rushingYards?: number | null;
	rushingYardsAverage?: number | null;
	rushingAttempts?: number | null;
	receivingTouchdowns?: number | null;
	receivingYards?: number | null;
	receivingYardsAverage?: number | null;
	targets?: number | null;
	receivingReceptions?: number | null;
	tackles?: number | null;
	sacks?: number | null;
	fumblesForced?: number | null;
	interceptions?: number | null;
	defensiveTouchdowns?: number | null;
	fieldGoalsPct?: number | null;
	kickoffReturnsAverage?: number | null;
	specialTeamsTackles?: number | null;
	puntReturnsAverage?: number | null;
	returnsTouchdowns?: number | null;
}

const checkPlayerPosition = (player: IStatsTableItem, position: PlayerPositionEnum | number) => {
	if (position === 0) {
		return true;
	}

	return player.position === position;
};

const checkStatsSearchValue = (searchValue: string, item: IStatsTableItem) => {
	if (item.firstName || item.lastName) {
		return (
			// player
			item.firstName?.toLowerCase().includes(searchValue) ||
			item.lastName?.toLowerCase().includes(searchValue)
		);
	}

	// squad
	return item.name.toLowerCase().includes(searchValue);
};

const checkStatsTeamFilter = (team: number, item: IStatsTableItem) => {
	return item.squadId === team;
};

export const getStatsPlayersList = (players: IPlayer[], isMobile: boolean): IStatsTableItem[] =>
	players.map((player) => {
		const posKeys = playerPositions;

		return {
			id: player.id,
			firstName: player.firstName,
			lastName: player.lastName,
			squadId: player.squadId,
			name: getPlayerName(player),
			team: "",
			pos: get(posKeys, player.position, "-"),
			position: player.position,
			passingTouchdowns: get(player.stats, "passingTouchdowns"),
			passingYards: get(player.stats, "passingYards"),
			passingYardsAverage: get(player.stats, "passingYardsAverage"),
			passingCompletionsPercentage: get(
				player.stats,
				"passingCompletionsPercentage"
			)?.toFixed(1),
			passingInterceptions: get(player.stats, "passingInterceptions"),
			rushingTouchdowns: get(player.stats, "rushingTouchdowns"),
			rushingYards: get(player.stats, "rushingYards"),
			rushingYardsAverage: get(player.stats, "rushingYardsAverage"),
			rushingAttempts: get(player.stats, "rushingAttempts"),
			receivingTouchdowns: get(player.stats, "receivingTouchdowns"),
			receivingYards: get(player.stats, "receivingYards"),
			receivingYardsAverage: get(player.stats, "receivingYardsAverage"),
			targets: get(player.stats, "targets"),
			receivingReceptions: get(player.stats, "receivingReceptions"),
			tackles: get(player.stats, "tackles"),
			sacks: get(player.stats, "sacks"),
			fumblesForced: get(player.stats, "fumblesForced"),
			interceptions: get(player.stats, "interceptions"),
			defensiveTouchdowns: get(player.stats, "defensiveTouchdowns"),
			fieldGoalsPct: get(player.stats, "fieldGoalsPct"),
			kickoffReturnsAverage: get(player.stats, "kickoffReturnsAverage"),
			specialTeamsTackles: get(player.stats, "specialTeamsTackles"),
			puntReturnsAverage: get(player.stats, "puntReturnsAverage"),
			returnsTouchdowns: get(player.stats, "returnsTouchdowns"),
		};
	});

export const getStatsList = (
	players: IPlayer[],
	squads: ISquad[],
	filters: IStatsFilter,
	isMobile: boolean,
	statsCentreTableRows: IStatCell[],
	isFilterByValue: boolean
) => {
	let list: IStatsTableItem[] = getStatsPlayersList(players, isMobile).filter((item) =>
		checkPlayerPosition(item, filters.position)
	);

	const searchValue = filters.search.toLowerCase();
	list = list.filter((item) => checkStatsSearchValue(searchValue, item));

	if (filters.team) {
		list = list.filter((item) => checkStatsTeamFilter(filters.team, item));
	}

	if (isFilterByValue) {
		list = list.filter((item) => {
			return (
				statsCentreTableRows.filter((row) => {
					return !isUndefined(get(item, row.id));
				}).length > 0
			);
		});
	}

	const order = filters.direction === DirectionFilter.High ? "asc" : "desc";

	return orderBy(
		list,
		(item) => {
			return get(item, filters.sortStat, "");
		},
		order
	);
};
